import React, { useState, useEffect } from 'react';
import { Box, Container, IconButton, Typography, CircularProgress, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import ConversationPanel from '../components/enhancement/ConversationPanel';
import BulletsPanel from '../components/enhancement/BulletsPanel';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';

/**
 * Enhancement Flow Page
 * 
 * Split-panel interface for conversational bullet enhancement
 * Phase 1.2: Real conversation API + mocked bullets
 */
const EnhancementFlowPage = () => {
  const navigate = useNavigate();
  
  // Get conversationId from URL search params
  const searchParams = new URLSearchParams(window.location.search);
  const urlConversationId = searchParams.get('conversationId');
  
  // State
  const [conversationId, setConversationId] = useState(urlConversationId || null);
  const [topic, setTopic] = useState('');
  const [messages, setMessages] = useState([]);
  const [bullets, setBullets] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Calculate progress based on bullet scores
  const progress = React.useMemo(() => {
    const strongBullets = bullets.filter(bullet => {
      const total = Object.values(bullet.scores).reduce((a, b) => a + b, 0);
      return total >= 12 && bullet.goalAlignment.evidenceStrength === 'strong';
    });
    return {
      current: strongBullets.length,
      target: 5
    };
  }, [bullets]);

  // Load or start conversation on mount
  useEffect(() => {
    const loadOrStartConversation = async () => {
      if (conversationId) {
        // Load existing conversation
        try {
          setIsLoading(true);
          setError(null);

          const response = await fetch(
            `${API_URL}/api/profile/enhancement/conversations/${conversationId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...mockAuthHeaders()
              },
              credentials: 'include'
            }
          );

          if (!response.ok) {
            throw new Error('Failed to load conversation');
          }

          const data = await response.json();
          
          setTopic(data.topic);
          setMessages(data.messages);
          setBullets(data.bullets || []);
          setIsLoading(false);
        } catch (err) {
          console.error('[Enhancement] Error loading conversation:', err);
          setError('Failed to load conversation. Please try again.');
          setIsLoading(false);
        }
        return;
      }

      // No conversation ID - show message
      setIsLoading(false);
      setError('No conversation found. Please start a new conversation from the Resume Builder.');
    };

    loadOrStartConversation();
    // Only run on mount or when conversationId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Send message handler (Real API)
  const handleSendMessage = async (content) => {
    if (!conversationId) {
      console.error('[Enhancement] No conversation ID');
      return;
    }

    setIsProcessing(true);
    setError(null);

    // Optimistically add user message
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      const response = await fetch(
        `${API_URL}/api/profile/enhancement/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...mockAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({
            content
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      
      setMessages(data.messages);
      setBullets(data.bullets || []);
      setIsProcessing(false);
    } catch (err) {
      console.error('[Enhancement] Error sending message:', err);
      setError('Failed to send message. Please try again.');
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
      setIsProcessing(false);
    }
  };

  // Save handler for individual bullets
  const handleSaveBullet = (bullet, position) => {
    if (position) {
      alert(`✅ Saved to Profile!\n\nPosition: ${position.title}\nCompany: ${position.company}\n\nBullet: "${bullet.text}"`);
    }
  };

  const handleClose = () => {
    navigate('/profile');
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Starting conversation...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)', // Account for AppBar height
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fefdfb',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e5e7eb',
          bgcolor: 'white',
          flexShrink: 0
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3a26' }}>
          Enhancement Flow - {topic}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Split Panel Layout */}
      <Container
        maxWidth={false}
        sx={{
          flex: 1,
          display: 'flex',
          gap: 0,
          p: 0,
          overflow: 'hidden'
        }}
      >
        {/* Left Panel: Conversation */}
        <Box
          sx={{
            width: '50%',
            borderRight: '1px solid #e5e7eb',
            bgcolor: 'white'
          }}
        >
          <ConversationPanel
            messages={messages}
            onSendMessage={handleSendMessage}
            isProcessing={isProcessing}
          />
        </Box>

        {/* Right Panel: Bullets */}
        <Box
          sx={{
            width: '50%',
            bgcolor: '#fefdfb'
          }}
        >
          <BulletsPanel bullets={bullets} progress={progress} onSaveBullet={handleSaveBullet} />
        </Box>
      </Container>
    </Box>
  );
};

export default EnhancementFlowPage;
